import { call, put, select, takeLatest } from 'redux-saga/effects';
import axios from 'axios';
import { v4 as uuid } from 'uuid';
import { detect, BrowserInfo, SearchBotDeviceInfo, BotInfo, NodeInfo } from 'detect-browser';
import { INTENT_ACTION, IntentState } from '~redux/reducers/intent';
import { ERROR_ACTIONS } from '~redux/reducers/error';
import { buildPaymentMethodBody } from './paymentMethod';
import { getRequest, getIntent } from '~redux/selectors';
import { getCheckoutApiUrl, getAirwayUrl } from '~components/utils';
import { POST_EVENTS, sendEvent, NextAction } from '~components/events';
import { RequestType } from '../reducers/request';
import { ExtendElementType } from '~components/events';
import { clientRetrieveItem, sendRequestLog } from '~components/utils';

export interface Address {
  country_code?: string;
  state?: string;
  city?: string;
  postcode?: string;
  street?: string;
}

export interface BillingType {
  email?: string;
  first_name?: string;
  last_name?: string;
  address?: Address;
}

export interface ConfirmDataType {
  cardnumber?: string;
  expiry?: string;
  cvc?: string;
  cardname?: string;
  id: string;
  token?: string;
  payment_consent_id?: string;
  componentName: ExtendElementType;
  billing?: BillingType;
  use_dcc?: string;
  nextAction?: NextAction;
  save_payment_method?: boolean;
}

export const getDeviceInfo = async (deviceId: string, origin: string) => {
  const browser = detect() || ({} as BrowserInfo | SearchBotDeviceInfo | BotInfo | NodeInfo | null);
  return {
    browser_info: `${browser?.name}/${browser?.version} ${browser?.os}`,
    device_id: deviceId,
    http_browser_type: browser?.name,
    // Workaround due to BE ticket: https://airwallex.atlassian.net/browse/APA-217
    cookies_accepted: true,
    host_name: origin,
  };
};

export const buildHeaders = (request: { client_secret: string }) => ({
  withCredentials: false,
  headers: {
    'client-secret': request.client_secret,
    'Content-Type': 'application/json',
    'Awx-Tracker': clientRetrieveItem('awx_tracker'),
    'x-client-ip-source': 'awx-checkout',
  },
});

export const sendOnChallengeEvent = (
  request: RequestType,
  nextAction: NextAction,
  componentName: ExtendElementType,
  cardBin: string,
  id: string,
) => {
  const eventPartialBody = {
    code: POST_EVENTS.onChallenge,
    intent: {
      client_secret: request?.client_secret,
      id,
    },
    next_action: nextAction,
    cardBin,
    auto_capture: request?.autoCapture,
  };
  if (window.location.href.indexOf('standalone') !== -1) {
    const customEvent = new CustomEvent('message', {
      detail: {
        type: 'hpp',
        ...eventPartialBody,
      },
      bubbles: true,
      composed: true,
    });
    window.document.dispatchEvent(customEvent);
  } else {
    sendEvent(
      {
        type: componentName,
        ...eventPartialBody,
      },
      request.origin,
    );
  }
};

export function* buildConfirmBody(payload: ConfirmDataType) {
  const request = yield select(getRequest);
  const { customer_id, autoCapture } = request;
  const device = yield getDeviceInfo(request.deviceId, request.origin);
  const paymentMethod = yield buildPaymentMethodBody(payload);
  const body = {
    request_id: uuid(),
    device,
    save_payment_method: payload?.save_payment_method || false,
    payment_method: paymentMethod,
    payment_method_options: {
      card: {
        auto_capture: `${autoCapture}`.toLocaleLowerCase() === 'true',
        three_ds: {
          return_url: `${getCheckoutApiUrl()}/elements/3ds?origin=${window.location.origin}&id=${
            payload.id
          }&client_secret=${request.client_secret}&checkoutOrigin=${request.origin || window.location.origin}`,
        },
      },
    },
    customer_id: customer_id,
  };
  return body;
}

interface CheckoutResponseFlowProps {
  id: string;
  componentName: ExtendElementType;
  confirmation: IntentState;
  request: RequestType;
}

export function checkoutResponseFlow({ id, componentName, confirmation, request }: CheckoutResponseFlowProps) {
  if (confirmation?.next_action?.type === 'redirect') {
    const { bin } = confirmation?.latest_payment_attempt?.payment_method?.card || {};
    sendOnChallengeEvent(request, confirmation?.next_action, componentName, bin ?? '', id);
  } else if (confirmation?.next_action?.type === 'dcc') {
    sendEvent(
      {
        code: POST_EVENTS.onDynamicCurrencyConversion,
        type: componentName,
      },
      request.origin,
    );
  }
}

export function* checkoutInstrumentConfirmAsync(data: { type: string; payload: ConfirmDataType }) {
  yield put({ type: ERROR_ACTIONS.SET, payload: {} });
  const intent = yield select(getIntent);
  const { id, componentName } = data.payload;
  try {
    yield put({ type: INTENT_ACTION, payload: { ...intent, status: 'PROCESSING' } });
    const request = yield select(getRequest);
    const device = yield getDeviceInfo(request.deviceId, request.origin);
    const { customer_id, autoCapture } = request;
    const commonBody = {
      request_id: uuid(),
      customer_id: customer_id,
      device,
      payment_method_options: {
        card: {
          auto_capture: `${autoCapture}`.toLocaleLowerCase() === 'true',
        },
      },
      return_url: `${getCheckoutApiUrl()}/elements/3ds?origin=${window.location.origin}&id=${
        data.payload.id
      }&client_secret=${request.client_secret}&checkoutOrigin=${request.origin || window.location.origin}`,
    };
    const paymentMethodReference = {
      id: data.payload.token,
      cvc: (data.payload.cvc || '').trim(),
    };
    const paymentConsentReference = {
      id: data.payload.payment_consent_id,
      cvc: (data.payload.cvc || '').trim(),
    };
    const reqPath = `${getAirwayUrl()}/payment_intents/${data.payload.id}/confirm`;
    const reqPayload = {
      ...commonBody,
      payment_consent_reference: data.payload.payment_consent_id ? paymentConsentReference : null,
      /**
       * We do not deprecate confirming an intent via paymentMethodId
       */
      payment_method_reference: data.payload.token && !data.payload.payment_consent_id ? paymentMethodReference : null,
    };
    const reqHeader = buildHeaders(request);
    sendRequestLog({ componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
    const { data: confirmation } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
    sendEvent(
      {
        type: componentName,
        code: POST_EVENTS.onLogResponse,
        path: reqPath,
        res: confirmation,
        header: {
          ...reqHeader,
          'Awx-Tracker': null,
        },
      },
      request.origin,
    );
    checkoutResponseFlow({
      id,
      componentName,
      confirmation,
      request,
    });
    if (confirmation?.next_action?.type !== 'redirect') {
      yield put({ type: INTENT_ACTION, payload: confirmation });
    }
  } catch (error) {
    yield put({ type: INTENT_ACTION, payload: { ...intent, status: '' } });
    yield put({ type: ERROR_ACTIONS.SET, payload: error?.response?.data });
  }
}

export function* checkoutOneOffConfirmAsync(data: { type: string; payload: ConfirmDataType }) {
  yield put({ type: ERROR_ACTIONS.SET, payload: {} });
  const { id, componentName } = data.payload;
  const intent = yield select(getIntent);
  try {
    yield put({ type: INTENT_ACTION, payload: { ...intent, status: 'PROCESSING' } });
    console.log('checkout confirm', new Date().toLocaleTimeString());
    const reqPayload = yield buildConfirmBody(data.payload as ConfirmDataType);
    const request = yield select(getRequest);

    const reqPath = `${getAirwayUrl()}/payment_intents/${data.payload.id}/confirm`;
    const reqHeader = buildHeaders(request);
    sendRequestLog({ componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
    const { data: confirmation } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
    sendEvent(
      {
        type: componentName,
        code: POST_EVENTS.onLogResponse,
        path: reqPath,
        res: confirmation,
        header: {
          ...reqHeader,
          'Awx-Tracker': null,
        },
      },
      request.origin,
    );
    checkoutResponseFlow({
      id,
      componentName,
      confirmation,
      request,
    });
    if (confirmation?.next_action?.type !== 'redirect') {
      yield put({ type: INTENT_ACTION, payload: confirmation });
    }
  } catch (error) {
    yield put({ type: INTENT_ACTION, payload: { ...intent, status: '' } });
    yield put({ type: ERROR_ACTIONS.SET, payload: error?.response?.data });
  }
}

export function* checkoutConfirmContinueForDcc(data: { type: string; payload: ConfirmDataType }) {
  yield put({ type: ERROR_ACTIONS.SET, payload: {} });
  const intent = yield select(getIntent);
  try {
    const { id, use_dcc, nextAction, componentName } = data?.payload || {};
    yield put({ type: INTENT_ACTION, payload: { ...intent, status: 'PROCESSING', next_action: nextAction } });
    const request = yield select(getRequest);
    const reqPayload = {
      request_id: uuid(),
      type: 'dcc',
      use_dcc,
    };
    const reqPath = `${getAirwayUrl()}/payment_intents/${id}/confirm_continue`;
    const reqHeader = buildHeaders(request);
    sendRequestLog({ componentName, reqPath, reqHeader, reqPayload, origin: request.origin });
    const { data: confirmation } = yield call([axios, 'post'], reqPath, reqPayload, reqHeader);
    sendEvent(
      {
        type: componentName,
        code: POST_EVENTS.onLogResponse,
        path: reqPath,
        res: confirmation,
        header: {
          ...reqHeader,
          'Awx-Tracker': null,
        },
      },
      request.origin,
    );
    if (confirmation.next_action && confirmation.next_action.type === 'redirect') {
      const { bin } = confirmation?.latest_payment_attempt?.payment_method?.card || {};
      sendOnChallengeEvent(request, confirmation?.next_action, componentName, bin, id);
    } else {
      yield put({ type: INTENT_ACTION, payload: confirmation });
    }
  } catch (error) {
    yield put({ type: INTENT_ACTION, payload: { ...intent, status: '' } });
    yield put({ type: ERROR_ACTIONS.SET, payload: error?.response?.data });
  }
}

export function* watchCheckoutOneOffConfirmAsync() {
  yield takeLatest('AWX_CHECKOUT_ONE_OFF_CONFIRM_ASYNC', checkoutOneOffConfirmAsync);
}

export function* watchCheckoutInstrumentConfirmAsync() {
  yield takeLatest('AWX_CHECKOUT_INSTRUMENT_CONFIRM_ASYNC', checkoutInstrumentConfirmAsync);
}

export function* watchCheckoutConfirmForDccAsync() {
  yield takeLatest('AWX_CHECKOUT_CONFIRM_CONTINUE_FOR_DCC_ASYNC', checkoutConfirmContinueForDcc);
}
